import React, { useState } from 'react';
import { Focus, Layers, Check } from 'lucide-react';
import type { AgentLensRequest, AgentLensRecommendation } from '@/core/ai/agent/IAgentTool';
import { Combobox } from '@/components/ui/combobox';
import { useTranslation } from '@/hooks/useTranslation';

interface LensRecommendationCardProps {
  lensRequest: AgentLensRequest;
  isActive: boolean;
  onApply: (selected: AgentLensRecommendation[]) => void;
}

interface RowState {
  checked: boolean;
  choiceIndex: number;
}

const toolIcon = (toolName: string) => {
  if (toolName === 'focus_view') return <Focus className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />;
  if (toolName === 'preset') return <Layers className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />;
  return <div className="h-3.5 w-3.5 shrink-0 rounded-full bg-muted-foreground/30" />;
};

function allChoices(rec: AgentLensRecommendation): Array<{ label: string; toolName: string; params: Record<string, unknown> }> {
  return [
    { label: rec.label, toolName: rec.toolName, params: rec.params },
    ...(rec.alternatives ?? []),
  ];
}

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

function translateLensDisplayText(raw: string, translate: TranslateFn): string {
  const trimmed = raw.trim();
  const exact: Array<[string, string]> = [
    ['Prepare cost reduction analysis', 'assistant.lensPhrasePrepareCostReductionTitle'],
    [
      'Focus on full org and visualize compensation before modelling cuts',
      'assistant.lensPhraseFocusFullOrgCompDescription',
    ],
    ['Show full org', 'assistant.lensPhraseShowFullOrg'],
  ];
  for (const [en, key] of exact) {
    if (trimmed === en) {
      return translate(key, { defaultValue: en }) as string;
    }
  }

  const presets: Array<[string, string]> = [
    ['Span of Control', 'assistant.lensPresetSpanOfControl'],
    ['Performance Distribution', 'assistant.lensPresetPerformanceDistribution'],
    ['Compensation Bands', 'assistant.lensPresetCompensationBands'],
    ['Tenure Distribution', 'assistant.lensPresetTenureDistribution'],
    ['Flight Risk', 'assistant.lensPresetFlightRisk'],
    ['Succession Readiness', 'assistant.lensPresetSuccessionReadiness'],
    ['Vacancy Highlight', 'assistant.lensPresetVacancyHighlight'],
  ];
  const applyPrefix = 'Apply ';
  for (const [en, key] of presets) {
    if (trimmed === en) {
      return translate(key, { defaultValue: en }) as string;
    }
    if (trimmed === `${applyPrefix}${en}`) {
      const name = translate(key, { defaultValue: en }) as string;
      return translate('assistant.lensApplyNamedPreset', {
        name,
        defaultValue: `${applyPrefix}${en}`,
      }) as string;
    }
  }
  return raw;
}

export const LensRecommendationCard: React.FC<LensRecommendationCardProps> = ({
  lensRequest,
  isActive,
  onApply,
}) => {
  const { t } = useTranslation('diagram');
  const [rows, setRows] = useState<Record<string, RowState>>(() =>
    Object.fromEntries(
      lensRequest.recommendations.map((r) => [r.id, { checked: r.selected, choiceIndex: 0 }]),
    ),
  );

  const setChecked = (id: string, checked: boolean) =>
    setRows((prev) => ({ ...prev, [id]: { ...prev[id], checked } }));

  const setChoice = (id: string, choiceIndex: number) =>
    setRows((prev) => ({ ...prev, [id]: { ...prev[id], choiceIndex } }));

  const selectedCount = Object.values(rows).filter((r) => r.checked).length;

  const handleApply = () => {
    if (selectedCount === 0 || !isActive) return;
    const selected = lensRequest.recommendations
      .filter((r) => rows[r.id]?.checked)
      .map((r) => {
        const idx = rows[r.id]?.choiceIndex ?? 0;
        const choice = allChoices(r)[idx] ?? allChoices(r)[0];
        const displayLabel = translateLensDisplayText(choice.label, t);
        return { ...r, label: displayLabel, toolName: choice.toolName, params: choice.params };
      });
    onApply(selected);
  };

  if (!isActive) {
    return (
      <div className="text-xs text-muted-foreground flex items-center gap-1">
        <span className="text-emerald-600">✓</span>
        <span>{t('assistant.lensViewApplied', { defaultValue: 'View applied.' })}</span>
      </div>
    );
  }

  const titleDisplay = translateLensDisplayText(lensRequest.title, t);
  const descriptionDisplay = lensRequest.description
    ? translateLensDisplayText(lensRequest.description, t)
    : undefined;

  return (
    <div className="rounded-md border border-border bg-background p-3 space-y-2.5">
      <div>
        <div className="text-xs font-medium text-foreground">{titleDisplay}</div>
        {descriptionDisplay && (
          <div className="text-[11px] text-muted-foreground mt-0.5">{descriptionDisplay}</div>
        )}
      </div>

      <div className="space-y-1">
        {lensRequest.recommendations.map((rec) => {
          const row = rows[rec.id] ?? { checked: rec.selected, choiceIndex: 0 };
          const choices = allChoices(rec);
          const hasAlternatives = choices.length > 1;
          const activeChoice = choices[row.choiceIndex] ?? choices[0];

          return (
            <div key={rec.id} className="flex items-center gap-2 rounded-md px-1.5 py-1">
              <button
                onClick={() => setChecked(rec.id, !row.checked)}
                className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors ${
                  row.checked
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-input bg-background'
                }`}
              >
                {row.checked && <Check className="h-2.5 w-2.5" strokeWidth={3} />}
              </button>

              {toolIcon(activeChoice.toolName)}

              {hasAlternatives ? (
                <div className="flex-1 min-w-0">
                  <Combobox
                    options={choices.map((c, i) => ({
                      value: String(i),
                      label: translateLensDisplayText(c.label, t),
                    }))}
                    value={String(row.choiceIndex)}
                    onValueChange={(v) => setChoice(rec.id, Number(v))}
                    triggerClassName="h-7 text-xs px-2"
                  />
                </div>
              ) : (
                <span className="flex-1 text-xs text-foreground">
                  {translateLensDisplayText(activeChoice.label, t)}
                </span>
              )}
            </div>
          );
        })}
      </div>

      <button
        onClick={handleApply}
        disabled={selectedCount === 0}
        className="w-full rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground disabled:opacity-40 hover:bg-primary/90 transition-colors"
      >
        {selectedCount > 0
          ? (t('assistant.lensApplyViewWithCount', {
              count: selectedCount,
              defaultValue: `Apply view (${selectedCount})`,
            }) as string)
          : (t('assistant.lensApplyView', { defaultValue: 'Apply view' }) as string)}
      </button>
    </div>
  );
};
